import { Box, Typography } from "@material-ui/core";
import Highcharts from "highcharts";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import StackChart from "src/components/ScoringChart/Stack/StackChart";
import { baseBeautifulTooltip } from "src/components/ScoringChart/tool-utils/BeautifulTooltip";
import { FigureTypography } from "src/components/Typography/TitleTypography";
import OverviewMonitoringWrapper from "src/components/Wrapper/OverviewMonitoringWrapper";
import { MONITORING } from "src/config/constants";
import {
  useAnalysisOverviewChartSelector,
  useAnalysisOverviewSelector,
} from "src/context/monitoring/analysisOverviewContext";
import { numberWithCommas, toFixed } from "src/services/utils";

export default function TransactionVolumeOverview() {
  const { pool } = useParams();
  const { fetchTransactionVolume } = useAnalysisOverviewSelector((state) => state.fetch);
  const transactionVolume = useAnalysisOverviewChartSelector(MONITORING.OVERVIEW.transactionVolume, pool);

  useEffect(() => {
    fetchTransactionVolume(pool, { number: 90, interval: 8 }, { active: 2, isCheckMetadata: false });
  }, [pool]);

  const { data, date } = useMemo(() => {
    if (transactionVolume) {
      return {
        date: Highcharts.dateFormat(
          "%b %e, %Y, %k:%M",
          Number(transactionVolume["timestamps"].slice(-1)[0].split("-")[1])
        ),
        data: [
          { name: "deposit", data: transactionVolume["deposit"].slice(-1)[0] },
          { name: "borrow", data: transactionVolume["borrow"].slice(-1)[0] },
          { name: "withdraw", data: transactionVolume["withdraw"].slice(-1)[0] },
          { name: "repay", data: transactionVolume["repay"].slice(-1)[0] },
        ],
      };
    }
    return {
      date: "",
      data: [],
    };
  }, [transactionVolume]);

  const { categories, series, isLoading } = useMemo(() => {
    if (transactionVolume) {
      return {
        categories: [].concat(transactionVolume["timestamps"]),
        series: [
          {
            name: "deposit",
            data: transactionVolume["deposit"].map((element) => {
              return [].concat(element);
            }),
          },
          {
            name: "borrow",
            data: transactionVolume["borrow"].map((element) => {
              return [].concat(element);
            }),
          },
          {
            name: "withdraw",
            data: transactionVolume["withdraw"].map((element) => {
              return [].concat(element);
            }),
          },
          {
            name: "repay",
            data: transactionVolume["repay"].map((element) => {
              return [].concat(element);
            }),
          },
        ],
        isLoading: false,
      };
    }
    return {
      categories: [],
      series: [],
      isLoading: true,
    };
  }, [transactionVolume]);

  return (
    <OverviewMonitoringWrapper
      isLoading={isLoading}
      title="Transaction Volume"
      overview="The chart compares the daily transaction volume of Deposit, Borrow, Withdraw and Repay transactions in a lending pool."
      to={`/overview-analysis/transaction-volume/${pool}`}
    >
      <Box width="100%">
        <Box display="flex" flexWrap="wrap">
          {data.map((element, index) => {
            return (
              <FigureTypography
                key={index}
                value={`$${numberWithCommas(toFixed(element["data"], 2))}`}
                title={element.name}
                index={index}
                style={{ marginRight: "1rem" }}
              />
            );
          })}
        </Box>
        <Typography>{date}</Typography>
      </Box>
      <StackChart
        categories={categories}
        series={series}
        option={{
          chart: {
            height: "230px",
          },
          xAxis: {
            visible: false,
          },
          ...baseBeautifulTooltip(
            (value) => `$${numberWithCommas(toFixed(value, 2))}`,
            true,
            (value) => {
              const times = value.split("-");
              let time1 = times[0].length === 0 ? null : Highcharts.dateFormat("%b %e, %Y, %k:%M", Number(times[0]));
              let time2 = Highcharts.dateFormat("%b %e, %Y, %k:%M", Number(times[1]));
              let result = time1 ? `<div>From: ${time1}</div>` : "";
              result += `<div>To: ${time2}</div>`;
              return result;
            }
          ),
        }}
      />
    </OverviewMonitoringWrapper>
  );
}
